import { getCodeById } from './statusMapper';

const toStringValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const toNumberValue = (value: unknown, fallback: number = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const extractDateValue = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    const obj = value as Record<string, any>;
    if (typeof obj.start === 'string') return obj.start;
    if (typeof obj.end === 'string') return obj.end;
    if (typeof obj.start_date === 'string') return obj.start_date;
    if (obj.date && typeof obj.date.start === 'string') return obj.date.start;
    if (obj.date && typeof obj.date.end === 'string') return obj.date.end;
  }
  return '';
};

export const normalizeReservationRecord = (input: Record<string, any> | null | undefined): Record<string, any> | null => {
  if (!input || typeof input !== 'object') return null;

  const normalized: Record<string, any> = { ...input };

  const pageId = toStringValue(input.page_id) || toStringValue(input.pageId);
  const id = toStringValue(input.id) || pageId || toStringValue(input.resource_page_id) || toStringValue(input.pageID);
  if (!id) return null;

  const contactId = toStringValue(input.contact_id) || toStringValue(input.contactId) || toStringValue(input.contactID);
  const arrivalFlight = toStringValue(input.arrival_flight) || toStringValue(input.arrivalFlight) || toStringValue(input.arrival);
  const departureFlight = toStringValue(input.departure_flight) || toStringValue(input.departureFlight) || toStringValue(input.departure);
  const contactPlan = toStringValue(input.contact_plan);
  const invoiceLink = toStringValue(input.invoice_link) || toStringValue(input.invoiceLink);
  const comment = toStringValue(input.comment) || toStringValue(input.comments);
  const memo = toStringValue(input.memo) || toStringValue(input.notes) || toStringValue(input.memo1);
  const mail = toStringValue(input.mail) || toStringValue(input.email) || toStringValue(input.customer_email) || toStringValue(input.customerEmail);
  const tel = toStringValue(input.tel) || toStringValue(input.phone) || toStringValue(input.phone_number) || toStringValue(input.customer_tel) || toStringValue(input.customerTel);
  const bookingKind = toStringValue(input.bk) || toStringValue(input.booking_kind);
  // resource_id is the primary identifier for which vehicle/row the reservation belongs to
  // resource_id === vehicle_id (same value, legacy naming)
  const resourceId = toStringValue(input.resource_id) || toStringValue(input.resourceId) || toStringValue(input.vehicle_id) || toStringValue(input.vehicleId);
  const vehicleId = resourceId; // vehicle_id is an alias for resource_id
  const vehicleName = toStringValue(input.vehicle_name) || toStringValue(input.vehicleName);
  const rawStatus = toStringValue(input.status_code) || toStringValue(input.status);
  const statusId = toStringValue(input.status_id) || toStringValue(input.statusId);
  const statusCode = rawStatus || (statusId ? getCodeById(statusId) : null);
  const statusName = toStringValue(input.status_name);
  const bgColor = toStringValue(input.bg_color);
  const textColor = toStringValue(input.text_color);
  const customerName = toStringValue(input.customer_name) || toStringValue(input.cname);
  const customerTel = toStringValue(input.customer_tel) || tel;
  const customerEmail = toStringValue(input.customer_email) || mail;

  const pax = toNumberValue(input.pax ?? input.detail?.pax);
  const paxChildren = toNumberValue(input.pax_children ?? input.detail?.pax_children);
  const childSeat = toNumberValue(input.child_seat ?? input.detail?.child_seat ?? input.childSeat);
  const boosterSeat = toNumberValue(input.booster_seat ?? input.detail?.booster_seat ?? input.boosterSeat);

  const startDate = extractDateValue(input.start_date) || extractDateValue(input.start);
  const endDate = extractDateValue(input.end_date) || extractDateValue(input.end);

  normalized.id = id;
  normalized.page_id = pageId || id;
  normalized.contact_id = contactId;
  normalized.pax = pax;
  normalized.pax_children = paxChildren;
  normalized.child_seat = childSeat;
  normalized.booster_seat = boosterSeat;

  if (startDate) {
    normalized.start_date = startDate;
    normalized.start = startDate;
  }

  if (endDate) {
    normalized.end_date = endDate;
    normalized.end = endDate;
  }

  if (arrivalFlight !== undefined) normalized.arrival_flight = arrivalFlight;
  if (departureFlight !== undefined) normalized.departure_flight = departureFlight;
  if (contactPlan !== undefined) normalized.contact_plan = contactPlan;
  if (invoiceLink !== undefined) normalized.invoice_link = invoiceLink;
  if (comment !== undefined) normalized.comment = comment;
  if (memo !== undefined) normalized.memo = memo;
  if (mail !== undefined) normalized.mail = mail;
  if (tel !== undefined) normalized.tel = tel;
  if (bookingKind !== undefined) normalized.bk = bookingKind;
  if (resourceId !== undefined) {
    normalized.resource_id = resourceId;
    normalized.resourceId = resourceId;
  }
  if (vehicleId !== undefined) {
    normalized.vehicle_id = vehicleId;
    normalized.vehicleId = vehicleId;
  }
  if (vehicleName !== undefined) {
    normalized.vehicle_name = vehicleName;
  }
  if (statusCode !== undefined) {
    normalized.status = statusCode;
    normalized.status_code = statusCode;
  }
  if (statusName !== undefined) normalized.status_name = statusName;
  if (bgColor !== undefined) normalized.bg_color = bgColor;
  if (textColor !== undefined) normalized.text_color = textColor;
  if (customerName !== undefined) normalized.customer_name = customerName;
  if (customerTel !== undefined) normalized.customer_tel = customerTel;
  if (customerEmail !== undefined) normalized.customer_email = customerEmail;

  // New fields v1.4.0+
  normalized.short_memo = toStringValue(input.short_memo);
  normalized.request_coming_time = toStringValue(input.request_coming_time);
  normalized.request_returning_time = toStringValue(input.request_returning_time);

  let jsonData: unknown = input.json_data;
  if (typeof jsonData === 'string' && jsonData.trim()) {
    try {
      jsonData = JSON.parse(jsonData);
    } catch (e) {
      console.warn('reservationNormalizer', 'failed to parse json_data', { id, error: e });
    }
  }
  normalized.json_data = jsonData;

  normalized.pax = pax;
  normalized.pax_children = paxChildren;
  const basePrice = Number.isFinite(Number(input.base_price)) ? Number(input.base_price) : null;
  const opPrice = Number.isFinite(Number(input.op_price)) ? Number(input.op_price) : null;
  const nocPrice = Number.isFinite(Number(input.noc_price)) ? Number(input.noc_price) : null;
  const cdwPrice = Number.isFinite(Number(input.cdw_price)) ? Number(input.cdw_price) : null;
  const sumPrice = Number.isFinite(Number(input.sumprice ?? input.sum_price)) ? Number(input.sumprice ?? input.sum_price) : null;
  const payment = input.payment === true || input.payment === 1 || input.payment === '1';
  const agreement = input.agreement === true || input.agreement === 1 || input.agreement === '1';
  // statusName, bgColor, textColor are already defined at the top
  // boosterSeat and childSeat are already defined above
  const options = toStringValue(input.options);
  if (basePrice !== null) normalized.base_price = basePrice;
  if (opPrice !== null) normalized.op_price = opPrice;
  if (nocPrice !== null) normalized.noc_price = nocPrice;
  if (cdwPrice !== null) normalized.cdw_price = cdwPrice;
  if (sumPrice !== null) normalized.sumprice = sumPrice;
  if (payment !== undefined) normalized.payment = payment;
  if (agreement !== undefined) normalized.agreement = agreement;
  if (statusName) normalized.status_name = statusName;
  if (bgColor) normalized.bg_color = bgColor;
  if (textColor) normalized.text_color = textColor;
  if (boosterSeat !== null) normalized.booster_seat = boosterSeat;
  if (childSeat !== null) normalized.child_seat = childSeat;
  if (options) normalized.options = options;

  return normalized;
};

export const normalizeReservationList = (list: any[] | unknown): any[] => {
  if (!Array.isArray(list)) return [];
  const normalized: Record<string, any>[] = [];
  list.forEach((item) => {
    const next = normalizeReservationRecord(item);
    if (next) normalized.push(next);
  });
  return normalized;
};
